import { useCallback } from 'react';
import { useAutomationHistoryDrawer } from '../../../hooks';
import {
  hideNotification,
  showLoadingNotification,
  showWarnNotification,
  updateSuccessNotification,
} from '@vrplatform/theme';
import type { Maybe } from '@vrplatform/utils';
import {
  showApiErrorNotification,
  useApiMutation,
  useInvalidateApiQueries,
} from '@vrplatform/data';
import { useReservationDetailDrawer } from '../useReservationDetailDrawer';

type Props = {
  connectionId: Maybe<string>;
  uniqueRef: Maybe<string>;
  appId: Maybe<string>;
  refreshType: 'show-sync' | 'show-notification' | 'silent';
};

export function useReservationRefetchMutation() {
  'use memo';

  const { mutateAsync: extract, isPending: loading } = useApiMutation(
    'post',
    '/connections/{id}/sync'
  );
  const { open: openAutomationHistoryDrawer } = useAutomationHistoryDrawer();
  const { close: closeReservationDetailDrawer } = useReservationDetailDrawer();
  const invalidate = useInvalidateApiQueries();

  const mutate = useCallback(
    async ({ appId, connectionId, uniqueRef, refreshType }: Props) => {
      if (!uniqueRef) {
        return showWarnNotification({
          title: 'Missing reservation reference',
          message:
            'The reservation reference is required to refetch the reservation. Please reach out to support if the issue persists.',
        });
      }

      if (!appId || appId === 'finalytic') {
        return showWarnNotification({
          title: 'Refetching disabled',
          message: 'Refetching is not available for manual reservations.',
        });
      }

      if (!connectionId) {
        return showWarnNotification({
          title: 'Missing connection',
          message:
            'The connection is required to refetch the reservation. Please reach out to support if the issue persists.',
        });
      }

      const notifyId = `reservation-refetch-${connectionId}-${uniqueRef}`;

      if (refreshType !== 'silent') {
        showLoadingNotification({ id: notifyId });
      }

      await extract({
        params: {
          path: {
            id: connectionId,
          },
        },
        body: {
          type: 'reservation',
          uniqueRef,
        },
      })
        .then((response) => {
          const result = response.data;
          if (refreshType === 'show-sync') {
            const syncId = result.id;
            hideNotification({ id: notifyId });
            closeReservationDetailDrawer('replace');
            openAutomationHistoryDrawer(syncId, {
              type: 'push',
              refreshQueryKeys: ['reservations'],
            });
          }

          if (refreshType === 'show-notification') {
            invalidate(['reservations']);
            updateSuccessNotification({
              id: notifyId,
              title: 'Reservation refetched',
              message: 'The reservation was successfully refetched.',
            });
          }
        })
        .catch((err) => {
          if (refreshType !== 'silent') {
            showApiErrorNotification({
              id: notifyId,
              error: err,
              title: 'Failed to refetch reservation',
              defaultMessage:
                'Failed to refetch reservation. Please reach out to support if the issue persists.',
            });
          }
        });
    },
    [
      extract,
      closeReservationDetailDrawer,
      openAutomationHistoryDrawer,
      invalidate,
    ]
  );

  return {
    mutate,
    loading,
  };
}
